import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Table, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BiShow, BiPrinter } from "react-icons/bi";
import { fetchMovimientosByCuenta, resetMovimientos } from "../../reducers/detalleComprobante_slice";

import CustomPrintModal from "../UI/CustomPrintModal";
import * as XLSX from "xlsx";

import ComprobanteViewModal from "./ComprobanteViewModal";

const MovimientosCuentaModal = ({ show, onHide, cuenta, fechaIni, fechaFin }) => {
  const dispatch = useDispatch();

  const { movimientosCuenta, movimientosStatus, movimientosError } = useSelector(
    (state) => state.detalleComprobantes
  );

  const [showComprobanteModal, setShowComprobanteModal] = useState(false);
  const [nroSeleccionado, setNroSeleccionado] = useState(null);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  useEffect(() => {
    if (!show) return;
    if (!cuenta?.codigo_cuenta) return;

    dispatch(
      fetchMovimientosByCuenta({
        codigo_cuenta: cuenta.codigo_cuenta,
        fechaIni,
        fechaFin,
      })
    );
  }, [show, cuenta?.codigo_cuenta, fechaIni, fechaFin, dispatch]);

  const handleClose = () => {
    dispatch(resetMovimientos());
    onHide && onHide();
  };

  const handleVerComprobante = (comprobante) => {
    const nro = comprobante?.nrocomprobante;
    if (!nro) return;
    setNroSeleccionado(nro);
    setShowComprobanteModal(true);
  };

  const totales = useMemo(() => {
    const arr = Array.isArray(movimientosCuenta) ? movimientosCuenta : [];
    const totalDebe = arr.reduce((acc, m) => acc + Number(m?.debe || 0), 0);
    const totalHaber = arr.reduce((acc, m) => acc + Number(m?.haber || 0), 0);
    return { totalDebe, totalHaber };
  }, [movimientosCuenta]);

  // ✅ helper: “alinear a la derecha” en impresión (texto con padding)
  const padRightLike = (num, width = 14) => {
    const s = Number(num || 0).toFixed(2);
    return s.padStart(width, " ");
  };

  const handleShowPreview = () => {
    const arr = Array.isArray(movimientosCuenta) ? movimientosCuenta : [];

    const head = [["Fecha", "Nro. Comprobante", "Glosa", "Debe", "Haber"]];
    const body = arr.map((m) => [
      String(m?.fecha || "").slice(0, 10),
      m?.comprobante?.nrocomprobante || "-",
      m?.glosa || m?.comprobante?.glosa || "",
      // ✅ padding para “derecha” en el print modal
      padRightLike(m?.debe),
      padRightLike(m?.haber),
    ]);

    body.push([
      "",
      "",
      "TOTALES",
      padRightLike(totales.totalDebe),
      padRightLike(totales.totalHaber),
    ]);

    setPrintContent({ head, body });
    setShowPreviewModal(true);
  };

  // ✅ Excel con encabezado de cuenta y rango
  const handleExportExcel = () => {
    const arr = Array.isArray(movimientosCuenta) ? movimientosCuenta : [];
    const codigo = cuenta?.codigo_cuenta || "cuenta";
    const nombre = cuenta?.nombre_cuenta || "";

    // Hoja “tabla” pero con encabezado arriba
    const rows = [];

    // Encabezado informativo
    rows.push([`CUENTA: ${codigo} - ${nombre}`]);
    if (fechaIni || fechaFin) {
      rows.push([`RANGO: ${fechaIni || "—"} a ${fechaFin || "—"}`]);
    }
    rows.push([]); // línea en blanco

    // Encabezados de columnas
    rows.push(["Fecha", "Nro. Comprobante", "Glosa", "Debe", "Haber"]);

    // Datos
    arr.forEach((m) => {
      rows.push([
        String(m?.fecha || "").slice(0, 10),
        m?.comprobante?.nrocomprobante || "-",
        m?.glosa || m?.comprobante?.glosa || "",
        Number(m?.debe || 0),
        Number(m?.haber || 0),
      ]);
    });

    // Totales
    rows.push([]);
    rows.push(["", "", "TOTALES", totales.totalDebe, totales.totalHaber]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // ✅ Formato numérico y “derecha” en Excel (col D y E)
    // (Se aplica como formato, Excel lo alinea por ser número)
    // Ajuste básico de ancho de columnas
    worksheet["!cols"] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 45 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Movimientos");

    XLSX.writeFile(workbook, `movimientos_${codigo}.xlsx`);
  };

  const disableActions =
    movimientosStatus === "loading" || !Array.isArray(movimientosCuenta);

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header
          closeButton
          className="d-flex align-items-center justify-content-between"
        >
          <Modal.Title>
            Movimientos - {cuenta?.codigo_cuenta} {cuenta?.nombre_cuenta || ""}
          </Modal.Title>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-success"
              size="sm"
              title="Imprimir lista"
              onClick={handleShowPreview}
              disabled={disableActions}
            >
              <BiPrinter />
            </Button>

            <Button
              variant="outline-primary"
              size="sm"
              title="Exportar a Excel"
              onClick={handleExportExcel}
              disabled={disableActions}
            >
              <i className="bi bi-file-spreadsheet"></i>
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {(fechaIni || fechaFin) && (
            <div className="mb-2">
              <strong>Rango:</strong> {fechaIni || "—"} a {fechaFin || "—"}
            </div>
          )}

          {movimientosStatus === "loading" && (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Cargando movimientos...</div>
            </div>
          )}

          {movimientosStatus === "failed" && (
            <Alert variant="danger">
              {typeof movimientosError === "string"
                ? movimientosError
                : "No se pudieron cargar los movimientos."}
            </Alert>
          )}

          {movimientosStatus !== "loading" && (
            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Nro. Comprobante</th>
                    <th>Glosa</th>
                    <th className="text-end">Debe</th>
                    <th className="text-end">Haber</th>
                    <th className="text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {!movimientosCuenta || movimientosCuenta.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        Sin movimientos
                      </td>
                    </tr>
                  ) : (
                    movimientosCuenta.map((m) => (
                      <tr key={m.id}>
                        <td>{String(m?.fecha || "").slice(0, 10)}</td>
                        <td>{m?.comprobante?.nrocomprobante || "-"}</td>
                        <td>{m?.glosa || m?.comprobante?.glosa || ""}</td>
                        <td className="text-end">
                          {Number(m?.debe || 0).toFixed(2)}
                        </td>
                        <td className="text-end">
                          {Number(m?.haber || 0).toFixed(2)}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Ver comprobante"
                            onClick={() => handleVerComprobante(m?.comprobante)}
                            disabled={!m?.comprobante?.nrocomprobante}
                          >
                            <BiShow />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={3} className="text-end">
                      Totales
                    </th>
                    <th className="text-end">{totales.totalDebe.toFixed(2)}</th>
                    <th className="text-end">{totales.totalHaber.toFixed(2)}</th>
                    <th></th>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <ComprobanteViewModal
        show={showComprobanteModal}
        onHide={() => setShowComprobanteModal(false)}
        nrocomprobante={nroSeleccionado}
      />

      <CustomPrintModal
        show={showPreviewModal}
        handleClose={() => setShowPreviewModal(false)}
        title1="MOVIMIENTOS DE CUENTA"
        title2={`${cuenta?.codigo_cuenta || ""} ${cuenta?.nombre_cuenta || ""}${
          fechaIni || fechaFin ? ` | Rango: ${fechaIni || "—"} a ${fechaFin || "—"}` : ""
        }`}
        content={printContent}
        copia={false}
      />
    </>
  );
};

export default MovimientosCuentaModal;
